import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';

import {
  getObjectiveStatusOptions,
  OBJECTIVE_CATEGORY_OPTIONS,
  OBJECTIVE_TYPE_OPTIONS,
} from '../objectives.constants';
import { ObjectiveStatusEnum } from '../objectives.enum';
import { ObjectiveResponse } from '../types/objective.type';

export const ALL_OBJECTIVES_FILTER_KEY = 'all';

export const statusFilterKey = (status: ObjectiveStatusEnum) => `status:${status}`;

export type ObjectiveFilterOption = {
  key: string;
  label: string;
  count: number;
  filterLogic: (objective: ObjectiveResponse) => boolean;
};

export const useObjectiveFilters = (
  objectives: ObjectiveResponse[] | undefined,
  initialFilterKeys: string[] = [],
) => {
  const { palette } = useTheme();
  const [selectedFilterKeys, setSelectedFilterKeys] = useState<string[]>(initialFilterKeys);

  const initialSelection = initialFilterKeys.join('|');

  useEffect(() => {
    setSelectedFilterKeys(initialSelection ? initialSelection.split('|') : []);
  }, [initialSelection]);

  const filterOptions: ObjectiveFilterOption[] = useMemo(() => {
    const objectiveList = objectives ?? [];
    const statusOptions = getObjectiveStatusOptions(palette);

    const categoryFilters = OBJECTIVE_CATEGORY_OPTIONS.map((option) => ({
      key: `category:${option.value}`,
      label: option.label,
      filterLogic: (objective: ObjectiveResponse) => objective.category === option.value,
    }));

    const typeFilters = OBJECTIVE_TYPE_OPTIONS.map((option) => ({
      key: `type:${option.value}`,
      label: option.label,
      filterLogic: (objective: ObjectiveResponse) => objective.type === option.value,
    }));

    const statusFilters = statusOptions.map((option) => ({
      key: statusFilterKey(option.value),
      label: option.label,
      filterLogic: (objective: ObjectiveResponse) => objective.status === option.value,
    }));

    const withCounts = [...categoryFilters, ...typeFilters, ...statusFilters]
      .map((filter) => ({
        ...filter,
        count: objectiveList.filter(filter.filterLogic).length,
      }))
      .filter((filter) => filter.count > 0);

    return [
      {
        key: ALL_OBJECTIVES_FILTER_KEY,
        label: 'All',
        count: objectiveList.length,
        filterLogic: () => true,
      },
      ...withCounts,
    ];
  }, [objectives, palette]);

  const activeFilters = filterOptions.filter(
    (filter) =>
      filter.key !== ALL_OBJECTIVES_FILTER_KEY && selectedFilterKeys.includes(filter.key),
  );

  const filteredObjectives = activeFilters.length
    ? (objectives ?? []).filter((objective) =>
        activeFilters.some((filter) => filter.filterLogic(objective)),
      )
    : objectives ?? [];

  const toggleFilterKey = (key: string) => {
    if (key === ALL_OBJECTIVES_FILTER_KEY) {
      setSelectedFilterKeys([]);
      return;
    }

    setSelectedFilterKeys((keys) =>
      keys.includes(key) ? keys.filter((selected) => selected !== key) : [...keys, key],
    );
  };

  const selectedKeys = activeFilters.length
    ? activeFilters.map((filter) => filter.key)
    : [ALL_OBJECTIVES_FILTER_KEY];

  return { filterOptions, selectedKeys, toggleFilterKey, filteredObjectives };
};
